import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import moment from 'moment';
import TimeSlotSelect from '../common/TimeSlotSelect';

const EditOvertimeSlot = ({
  slotDetail,
  showEdit,
  handleShowEdit,
  handleUpdateSlot,
}) => {
  const validationSchema = Yup.object().shape({
    slotCount: Yup.number()
      .typeError('Must be a number')
      .required('Slot count is required')
      .min(1, 'At least 1 slot required'),

    otStart: Yup.string().required('Start time is required'),

    otEnd: Yup.string()
      .required('End time is required')
      .test(
        'end-after-start',
        'End time must be after start time',
        function (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const { otStart } = this.parent;
          if (!otStart || !value) return true;
          const start = moment(otStart, 'HH:mm');
          const end = moment(value, 'HH:mm');
          return end.isAfter(start);
        }
      ),
  });

  return (
    <>
      <Button
        variant="white"
        className="btn btn-sm btn-white"
        onClick={() => handleShowEdit(true, slotDetail._id)}
      >
        <i className="mdi mdi-pencil" /> Edit
      </Button>

      <Modal show={showEdit} onHide={() => handleShowEdit(false)} centered>
        <Modal.Header>
          <Modal.Title>Edit Overtime Slot</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => handleShowEdit(false)}
          />
        </Modal.Header>

        <Formik
          enableReinitialize
          initialValues={{
            slotId: slotDetail?._id || '',
            slotCount: slotDetail?.slotCount || '',
            otStart: slotDetail?.otStart || '',
            otEnd: slotDetail?.otEnd || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleUpdateSlot(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    {/* Start Time */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Slot Start Time{' '}
                        <Tippy
                          content="Pick a time from the list, or type your own (e.g. 14:30)"
                          theme="light"
                        >
                          <span className="d-inline-block">
                            <i className="mdi mdi-information-outline" />
                          </span>
                        </Tippy>
                      </Form.Label>
                      <TimeSlotSelect
                        name="otStart"
                        labelFormat="24h"
                        value={
                          formikprops.values.otStart
                            ? {
                                label: formikprops.values.otStart,
                                value: formikprops.values.otStart,
                              }
                            : null
                        }
                        onChange={(option) => {
                          formikprops.setFieldValue(
                            'otStart',
                            option.value,
                            true
                          );
                          formikprops.setFieldTouched('otEnd', true, false); // mark as touched
                        }}
                      />

                      <ErrorMessage
                        name="otStart"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {/* End Time */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Slot End Time{' '}
                        <Tippy
                          content="Pick a time from the list, or type your own (e.g. 14:30)"
                          theme="light"
                        >
                          <span className="d-inline-block">
                            <i className="mdi mdi-information-outline" />
                          </span>
                        </Tippy>
                      </Form.Label>
                      <TimeSlotSelect
                        name="otEnd"
                        labelFormat="24h"
                        value={
                          formikprops.values.otEnd
                            ? {
                                label: formikprops.values.otEnd,
                                value: formikprops.values.otEnd,
                              }
                            : null
                        }
                        onChange={(option) => {
                          formikprops.setFieldValue(
                            'otEnd',
                            option.value,
                            true
                          ); // value + validate
                          formikprops.setFieldTouched('otEnd', true, false); // mark as touched
                        }}
                      />

                      <ErrorMessage
                        name="otEnd"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {/* Slot Count */}
                <Form.Group className="mb-3">
                  <Form.Label>Slot Count</Form.Label>
                  <Field
                    name="slotCount"
                    type="number"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="slotCount"
                    component="small"
                    className="text-danger"
                  />
                </Form.Group>
                <br />
                <h5 className="mb-0">
                  <b>Note:</b> Start and end times are displayed in 24-hour
                  format, similar to Excel
                </h5>
              </Modal.Body>

              <Modal.Footer className="d-flex justify-content-end">
                <Button variant="primary" type="submit">
                  Save
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

EditOvertimeSlot.propTypes = {
  slotDetail: PropTypes.shape({
    _id: PropTypes.string,
    slotCount: PropTypes.number,
    otStart: PropTypes.string,
    otEnd: PropTypes.string,
  }),
  showEdit: PropTypes.bool.isRequired,
  handleShowEdit: PropTypes.func.isRequired,
  handleUpdateSlot: PropTypes.func.isRequired,
};

export default EditOvertimeSlot;
